// REST + WebSocket clients. When VITE_API_BASE is unset the app runs in "sample mode"
// (no backend needed) so the surfaces are fully demonstrable. With a backend, set
// VITE_API_BASE / VITE_WS_BASE and provide a Cognito ID token via setAuthToken().

import { handleAuthExpired, AUTH_EXPIRED } from './reauth';
import type { AdminAnalytics, AuditEventView, BudgetDto, CampaignMeta, CohortRadar, CustomDimensionDefView, OrgBlend, OrgDimensionSetView, OrgSummary, PlatformSettingsView, PublicSettingsView, PracticeCompletion, PracticeScore, ReportView, RctoFields, SessionSummary, TemplatesResponse, TranscriptMessage, UserTemplateView } from '../types';
import type { TailoredPack } from '../assessment-steps';

const API_BASE = import.meta.env.VITE_API_BASE as string | undefined;
export const WS_BASE = import.meta.env.VITE_WS_BASE as string | undefined;

export const isSampleMode = !API_BASE;

let authToken: string | undefined;
export function setAuthToken(token: string) {
  authToken = token;
}

/** True if the JWT is missing an exp or already past it. Malformed tokens count as expired. */
export function tokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof payload.exp !== 'number' || payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!API_BASE) throw new Error('No API configured (sample mode)');
  // Pre-empt an expired token: the deployed gateway rejects it at the authorizer with a 401 that
  // carries NO CORS headers, so the browser throws a TypeError ("NetworkError…") and the 401
  // handler below never runs — the user would see a dead error instead of being re-authed. Route
  // it into the same reauth bounce a 401 response would trigger, before the doomed request.
  if (authToken && tokenExpired(authToken)) {
    handleAuthExpired();
    const err = new Error('401 token expired');
    (err as Error & { code?: string }).code = AUTH_EXPIRED;
    throw err;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'content-type': 'application/json',
      ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 || res.status === 403) {
    // session missing/expired/rejected — bounce once to reauth (guarded, no loop)
    handleAuthExpired();
    const err = new Error(`${res.status} auth expired`);
    (err as Error & { code?: string }).code = AUTH_EXPIRED;
    throw err;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

export const api = {
  createSession: (body?: { campaignId?: string; role?: string; consent?: { version: number } }) =>
    req<{ session: SessionSummary; resumed: boolean }>('POST', '/sessions', body ?? {}),
  // Role-tailor the interactive assessment; { pack: null } → use the default (presales) set.
  tailor: (id: string, role: string) =>
    req<{ pack: TailoredPack | null }>('POST', `/sessions/${id}/tailor`, { role }),
  archiveSession: (id: string) =>
    req<{ assessmentId: string; status: string }>('POST', `/sessions/${id}/archive`),
  // Interactive assessment: submit the structured answers; Avery scores them server-side.
  finalize: (id: string, answers: string[]) =>
    req<{ done: boolean }>('POST', `/sessions/${id}/finalize`, { answers }),
  getReport: (id: string) => req<{ report: ReportView; cohort?: CohortRadar }>('GET', `/sessions/${id}/report`),
  listSessions: () => req<{ sessions: SessionSummary[] }>('GET', '/sessions'),
  getSession: (id: string) =>
    req<{ session: SessionSummary; messages: TranscriptMessage[] }>('GET', `/sessions/${id}`),
  postMessage: (id: string, text: string) =>
    req<{ reply: { text: string }; stage: number; stageAdvanced: boolean; done: boolean; budgetNotice?: string }>(
      'POST',
      `/sessions/${id}/messages`,
      { text },
    ),
  scorePractice: (body: { draft: string; scenario?: string }) =>
    req<{ score: PracticeScore }>('POST', '/practice/score', body),
  listPracticeCompletions: () =>
    req<{ completions: PracticeCompletion[] }>('GET', '/practice/completions'),
  savePracticeCompletion: (body: {
    itemType: PracticeCompletion['itemType'];
    itemRef: string;
    done?: boolean;
    reflection?: string;
    assessmentId?: string;
  }) => req<{ completion: PracticeCompletion }>('POST', '/practice/completions', body),
  // Prompt-template library (role-resolved, model-generated + cached) + the caller's saved templates.
  listTemplates: (opts?: { regenerate?: boolean }) =>
    req<TemplatesResponse>('GET', `/templates${opts?.regenerate ? '?regenerate=1' : ''}`),
  saveTemplate: (body: { title: string; fields: RctoFields; fromTemplateId?: string }) =>
    req<{ template: UserTemplateView }>('POST', '/templates', body),
  deleteTemplate: (id: string) => req<{ deleted: string }>('DELETE', `/templates/${id}`),
  // The Organisations index. One campaign == one organisation; the path stays /campaigns
  // (no API-Gateway route change) but the body is the per-org summary list.
  listOrganisations: () => req<{ organisations: OrgSummary[] }>('GET', '/campaigns'),
  // Per-org dashboard data: analytics (k-anonymised, role-level) + spend, by campaign id.
  getCampaign: (id: string, showAllTeams: boolean, identified = false) =>
    req<{ analytics: AdminAnalytics; campaign?: CampaignMeta }>(
      'GET',
      `/campaigns/${id}?showAllTeams=${showAllTeams}&identified=${identified}`,
    ),
  getBudget: (id: string) => req<{ budget: BudgetDto }>('GET', `/campaigns/${id}/budget`),
  // Campaign lifecycle (admin). Create derives a slug id from the name when none is given.
  createCampaign: (body: Partial<Pick<CampaignMeta, 'campaignId' | 'name' | 'sector' | 'internal' | 'periodStart' | 'periodEnd' | 'status' | 'budgetUsd'>>) =>
    req<{ campaign: CampaignMeta }>('POST', '/campaigns', body),
  updateCampaign: (id: string, body: Partial<Pick<CampaignMeta, 'name' | 'sector' | 'internal' | 'periodStart' | 'periodEnd' | 'status' | 'budgetUsd'>>) =>
    req<{ campaign: CampaignMeta }>('PUT', `/campaigns/${id}`, body),
  // Publish (create/replace) an org's custom dimension set. Admin-only; server validates + versions.
  publishDimensions: (id: string, body: { dimensions: CustomDimensionDefView[]; blend: OrgBlend }) =>
    req<{ customDimensions: OrgDimensionSetView }>('POST', `/campaigns/${id}/dimensions`, body),
  // Platform settings. Participant-facing read (allow-listed), admin read (full), admin publish.
  getPublicSettings: () => req<{ settings: PublicSettingsView }>('GET', '/platform/settings'),
  getAdminSettings: () => req<{ settings: PlatformSettingsView }>('GET', '/admin/settings'),
  // Admin audit trail (privileged-action record), newest first.
  getAuditLog: (days = 90) => req<{ events: AuditEventView[] }>('GET', `/admin/audit?days=${days}`),
  publishSettings: (scope: 'experience' | 'assessment-structure' | 'branding', body: unknown) =>
    req<{ scope: string; settings: PlatformSettingsView }>('PUT', `/platform/settings/${scope}`, body),
  // Per-campaign settings (k-anonymity threshold, identified-roster policy, mode override).
  publishCampaignSettings: (id: string, body: Partial<Pick<CampaignMeta, 'kAnonymityThreshold' | 'identifiedRosterPolicy' | 'modeOverride'>>) =>
    req<{ campaign: CampaignMeta }>('PUT', `/campaigns/${id}/settings`, body),
  // DSAR: erase one assessment, or export all of the caller's own data.
  deleteAssessment: (id: string) =>
    req<{ assessmentId: string; deleted: boolean; itemsRemoved: number }>('DELETE', `/sessions/${id}`),
  exportMyData: () => req<unknown>('GET', '/sessions/export'),
};
